import React, { useState } from 'react';
import { useAuth, type AnonymousCredentials } from '../contexts/AuthContext';

interface AnonymousFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
  onSwitchToRegister?: () => void;
}

interface FormErrors {
  displayName?: string;
  general?: string;
}

export function AnonymousForm({ onSuccess, onSwitchToLogin, onSwitchToRegister }: AnonymousFormProps) {
  const { loginAnonymous, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<AnonymousCredentials>({
    displayName: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Validate form
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    // Display name validation
    if (!formData.displayName.trim()) {
      errors.displayName = 'Display name is required';
    } else if (formData.displayName.trim().length < 2) {
      errors.displayName = 'Display name must be at least 2 characters long';
    } else if (formData.displayName.trim().length > 50) {
      errors.displayName = 'Display name must be less than 50 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }

    // Clear general error
    if (error) {
      clearError();
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await loginAnonymous({
        displayName: formData.displayName.trim(),
      });
      onSuccess?.();
    } catch (err) {
      // Error is handled by the auth context
      console.error('Anonymous login failed:', err);
    }
  };

  return (
    <div className="anonymous-form">
      <h2>Continue as Guest</h2>
      <p className="form-description">
        Join the conversation without creating an account. Your session will be temporary.
      </p>
      
      <form onSubmit={handleSubmit} noValidate>
        {/* Display name field */}
        <div className="form-group">
          <label htmlFor="displayName">Choose a Display Name</label>
          <input
            type="text"
            id="displayName"
            name="displayName"
            value={formData.displayName}
            onChange={handleInputChange}
            className={formErrors.displayName ? 'error' : ''}
            placeholder="Enter a name others will see"
            disabled={isLoading}
            autoComplete="name"
            required
          />
          {formErrors.displayName && (
            <span className="error-message">{formErrors.displayName}</span>
          )}
          <small className="help-text">
            This name will be visible to other users nearby
          </small>
        </div>

        {/* General error message */}
        {error && (
          <div className="error-message general-error">
            {error}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          className="submit-button"
          disabled={isLoading}
        >
          {isLoading ? 'Joining...' : 'Join as Guest'}
        </button>
      </form>

      {/* Alternative actions */}
      <div className="form-actions">
        <div className="guest-info">
          <h4>Guest Session Limitations:</h4>
          <ul>
            <li>Session expires after 4 hours of inactivity</li>
            <li>No message history preservation</li>
            <li>Limited profile customization</li>
          </ul>
        </div>

        {onSwitchToLogin && (
          <button
            type="button"
            className="link-button"
            onClick={onSwitchToLogin}
            disabled={isLoading}
          >
            Sign in to your account
          </button>
        )}
        
        {onSwitchToRegister && (
          <button
            type="button"
            className="link-button"
            onClick={onSwitchToRegister}
            disabled={isLoading}
          >
            Create a permanent account
          </button>
        )}
      </div>
    </div>
  );
}

export default AnonymousForm;